/**
 * usePWA Hook
 *
 * Detects PWA installation capabilities and manages the install prompt
 * Handles both Chrome/Edge (beforeinstallprompt) and iOS (manual instructions)
 *
 * @module hooks/usePWA
 */

import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  promptDismissed: boolean;
  install: () => Promise<void>;
  dismiss: () => void;
  resetDismissal: () => void;
}

const STORAGE_KEY = 'bacchus-pwa-dismissed';

/**
 * Detects if the app is running on iOS
 */
function detectIOS(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}

/**
 * Detects if the app is running in standalone mode
 */
function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // Check iOS standalone mode
  const isIOSStandalone = (window.navigator as any).standalone === true;

  // Check PWA display mode
  const isPWADisplayMode = window.matchMedia('(display-mode: standalone)').matches;

  return isIOSStandalone || isPWADisplayMode;
}

/**
 * Get dismissal status from localStorage
 */
function getDismissed(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    return dismissed === 'true';
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[usePWA] Failed to read dismissal state:', err);
    return false;
  }
}

/**
 * Set dismissal status in localStorage
 */
function setDismissed(value: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[usePWA] Failed to save dismissal state:', err);
  }
}

/**
 * Custom hook for PWA installation management
 *
 * @example
 * const { isInstallable, install, isIOS } = usePWA();
 *
 * if (isInstallable && !isIOS) {
 *   return <button onClick={install}>Install App</button>
 * }
 */
export function usePWA(): PWAInstallState {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Detect iOS
    setIsIOS(detectIOS());

    // Detect standalone mode
    const standalone = detectStandalone();
    setIsStandalone(standalone);
    setIsInstalled(standalone);

    // Load dismissal state
    setPromptDismissed(getDismissed());

    // Listen for beforeinstallprompt event (Chrome, Edge, Samsung Internet)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);

      if (import.meta.env.DEV) console.log('[usePWA] App is installable');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      if (import.meta.env.DEV) console.log('[usePWA] App was installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Trigger the install prompt
   */
  const install = useCallback(async () => {
    if (!deferredPrompt) {
      if (import.meta.env.DEV) console.warn('[usePWA] No install prompt available');
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (import.meta.env.DEV) console.log(`[usePWA] User response: ${outcome}`);

      if (outcome === 'accepted') {
        if (import.meta.env.DEV) console.log('[usePWA] User accepted the install prompt');
        setIsInstalled(true);
      } else {
        if (import.meta.env.DEV) console.log('[usePWA] User dismissed the install prompt');
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[usePWA] Install prompt failed:', err);
    }
  }, [deferredPrompt]);

  /**
   * Dismiss the install prompt
   */
  const dismiss = useCallback(() => {
    setPromptDismissed(true);
    setDismissed(true);
    if (import.meta.env.DEV) console.log('[usePWA] Install prompt dismissed by user');
  }, []);

  /**
   * Reset the dismissal state (for testing or re-showing)
   */
  const resetDismissal = useCallback(() => {
    setPromptDismissed(false);
    setDismissed(false);
    if (import.meta.env.DEV) console.log('[usePWA] Install prompt dismissal reset');
  }, []);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isStandalone,
    promptDismissed,
    install,
    dismiss,
    resetDismissal,
  };
}
